import type { PaymentTermOption } from '@erp/shared';
import { Check, ChevronsUpDown, Loader2, RotateCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useSalesPaymentTerms } from '../api/use-payment-terms';

export interface PaymentTermSelectProps {
  id: string;
  value: string | null | undefined;
  disabled?: boolean;
  customLabel?: string | undefined;
  onChange: (term: PaymentTermOption) => void;
}

function normalized(value: string): string {
  return value
    .normalize('NFKD')
    .toLocaleLowerCase()
    .replace(/\p{Diacritic}/gu, '');
}

export function PaymentTermSelect({
  id,
  value,
  disabled = false,
  customLabel,
  onChange,
}: PaymentTermSelectProps): JSX.Element {
  const { t } = useTranslation();
  const query = useSalesPaymentTerms();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = query.data?.data.find((term) => term.id === value);
  const filtered = useMemo(() => {
    const needle = normalized(search.trim());
    if (needle === '') return query.data?.data ?? [];
    return (query.data?.data ?? []).filter((term) =>
      normalized(`${term.label} ${term.code} ${String(term.dueDays)}`).includes(needle),
    );
  }, [query.data, search]);

  const preview = (term: PaymentTermOption): string => {
    const due = t('paymentTerms.netDays', { count: term.dueDays });
    return Number(term.cashDiscountPct) > 0
      ? t('paymentTerms.discountPreview', {
          due,
          percent: term.cashDiscountPct,
          days: term.cashDiscountDays,
        })
      : due;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || query.isPending}
          className="h-auto min-h-11 w-full justify-between whitespace-normal py-2 font-normal"
        >
          <span className="min-w-0 break-words text-left">
            {query.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('common.loading')}
              </span>
            ) : selected === undefined ? (
              (customLabel ?? t('paymentTerms.customLegacy'))
            ) : (
              `${selected.label} · ${preview(selected)}`
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(26rem,calc(100vw-2rem))] p-2" align="start">
        {query.isError ? (
          <div className="space-y-2 p-2 text-sm">
            <p>{t('paymentTerms.loadError')}</p>
            <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>
              <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('paymentTerms.search')}
              aria-label={t('paymentTerms.search')}
              className="mb-2"
            />
            <div className="max-h-64 overflow-y-auto" role="listbox">
              {filtered.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">{t('paymentTerms.noResults')}</p>
              ) : (
                filtered.map((term) => (
                  <button
                    key={term.id}
                    type="button"
                    role="option"
                    aria-selected={term.id === value}
                    className="flex min-h-11 w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      onChange(term);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        term.id === value ? '' : 'opacity-0',
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{term.label}</span>
                      <span className="block text-xs text-muted-foreground">{preview(term)}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
